import { useEffect, useRef, useState } from 'react';
import { Heading, Text, Muted, Button, ThemeToggle, AudioButton } from '@fluent/ui';
import './VoicePilotReview.css';

const VOICES = ['nova', 'alloy', 'shimmer'] as const;
type VoiceId = (typeof VOICES)[number];

const PHRASES = [
  { slug: 's01-greeting', en: "Hello, what's your name?", ka: 'გამარჯობა, რა გქვია?' },
  { slug: 's01-mariam', en: 'My name is Mariam.', ka: 'მე მქვია მარიამი.' },
  { slug: 's01-nice', en: 'Nice to meet you.', ka: 'სასიამოვნოა შენი გაცნობა.' },
  { slug: 's01-whatname', en: 'What is your name?', ka: 'რა გქვია?' },
];

const VOICE_NOTES: Record<VoiceId, string> = {
  nova: 'Female · warm, energetic. Closest to Duolingo-vibe. Default pick.',
  alloy: 'Neutral · calm, gender-ambiguous. Easy to listen to repeatedly.',
  shimmer: 'Female · clearer, brighter. Slightly more "newsreader" than nova.',
};

export function VoicePilotReview() {
  const [pick, setPick] = useState<VoiceId | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingKey, setPlayingKey] = useState<string | null>(null);

  useEffect(() => {
    const a = new Audio();
    a.preload = 'auto';
    a.addEventListener('ended', () => setPlayingKey(null));
    audioRef.current = a;
    return () => {
      a.pause();
      audioRef.current = null;
    };
  }, []);

  const play = (voice: VoiceId, slug: string) => {
    const a = audioRef.current;
    if (!a) return;
    const key = `${voice}/${slug}`;
    if (playingKey === key) {
      a.pause();
      a.currentTime = 0;
      setPlayingKey(null);
      return;
    }
    a.src = `/voice-pilot/${voice}-${slug}.mp3`;
    a.currentTime = 0;
    setPlayingKey(key);
    void a.play().catch(() => setPlayingKey(null));
  };

  return (
    <main className="vpilot">
      <header className="vpilot__hero">
        <p className="vpilot__kicker">Phase 8 · Voice pilot</p>
        <Heading level={2} tone="inverse">
          აარჩიე ერთი ხმა მთელი აპისთვის
        </Heading>
        <Text tone="inverse" size="base" className="vpilot__lede">
          Pick one. Same voice ships across all A1 + A2 audio (no mixing). Tap each phrase, compare,
          then lock with the button at the bottom.
        </Text>
        <div className="vpilot__theme">
          <ThemeToggle />
        </div>
      </header>

      <section className="vpilot__grid">
        {VOICES.map((voice) => (
          <article key={voice} className="vpilot__card" data-picked={pick === voice || undefined}>
            <header className="vpilot__cardhead">
              <Heading level={3}>{voice}</Heading>
              <Muted>{VOICE_NOTES[voice]}</Muted>
            </header>
            <ul className="vpilot__phrases">
              {PHRASES.map((p) => {
                const key = `${voice}/${p.slug}`;
                const isPlaying = playingKey === key;
                return (
                  <li key={p.slug} className="vpilot__phrase">
                    <AudioButton
                      size="sm"
                      playing={isPlaying}
                      onClick={() => play(voice, p.slug)}
                      label={`Play ${voice} — ${p.en}`}
                    />
                    <div className="vpilot__lines">
                      <Text size="base" weight="bold">
                        {p.en}
                      </Text>
                      <Muted>{p.ka}</Muted>
                    </div>
                  </li>
                );
              })}
            </ul>
            <footer className="vpilot__cardfoot">
              <Button
                variant={pick === voice ? 'primary' : 'neutral'}
                size="md"
                fullWidth
                onClick={() => setPick(voice)}
              >
                {pick === voice ? '✓ Picked' : `Pick ${voice}`}
              </Button>
            </footer>
          </article>
        ))}
      </section>

      <section className="vpilot__lock" aria-live="polite">
        {pick ? (
          <Text size="base">
            Tornike picked <strong>{pick}</strong>. Confirm with Jake to lock in DECISIONS.md and
            run the full pipeline.
          </Text>
        ) : (
          <Muted>Tap each ▶, compare. No silent approval — say the voice name out loud.</Muted>
        )}
      </section>
    </main>
  );
}
